import React from 'react';
import Image from 'next/image';
import posthog from 'posthog-js';
import styled from 'styled-components';
import { ButtonPrimary } from '../../ui/atoms/Button';
import { Paragraph } from '../../ui/typography';
import Container from '../Container';
import { isUserLoggedIn } from '../../utils/analytics';
import { useStore } from '../../store';

const MastheadWrapper = styled.div<{ backgroundImage?: string }>`
  height: 670px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-image: ${({ backgroundImage }) => `url(${backgroundImage})`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    height: 700px;
  }
`;

const MastheadContainer = styled(Container)`
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  margin: 0 16px;
  margin-bottom: 5px;
  justify-content: space-between;
  width: 65%;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const DesktopPlushieImageWrapper = styled.div`
  display: none;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    display: block;
    width: 500px;
    height: 500px;
  }
`;

const MobilePlushieImageWrapper = styled.div`
  width: 282px;
  height: 282px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    display: none;
  }
`;

const DescriptionWrapper = styled.div<{ backgroundColor: string }>`
  background: ${({ backgroundColor }) => backgroundColor};
  display: flex;
  padding: 24px 16px;
  border-radius: 10px;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  max-width: 328px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    justify-content: space-between;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    max-width: 468px;
  }
`;

const LogoAndDescriptionWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
  justify-content: center;
`;

const InfoContentWrapper = styled.div``;

const ImageDesktopWrapper = styled.div`
  display: none;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    display: block;
    position: relative;
    width: 320px;
    height: 75px;
  }
`;

const DesktopLogoImage = styled(Image)`
  object-fit: contain;
  width: 100%;
  height: 100%;
`;

const MobileLogoImage = styled(Image)`
  object-fit: contain;
  width: 100%;
  height: 100%;
`;

const ImageMobileWrapper = styled.div`
  display: block;
  width: 218px;
  height: 51px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    display: none;
  }
`;

type MastheadCarouselItemProps = {
  descriptionText: string;
  descriptionTextColor: string;
  descriptionBackgroundColor: string;
  ctaText: string;
  ctaBackgroundColor: string;
  ctaLink: string;
  productImage: string;
  logoImage: string;
  backgroundImage: string;
  collectionName: string;
};

const getColorProp = (color: string): 'primary-black' | 'primary-white' =>
  color.toLowerCase() === 'white' ? 'primary-white' : 'primary-black';

const MastheadCarouselItem: React.FC<MastheadCarouselItemProps> = ({
  descriptionText,
  descriptionTextColor,
  descriptionBackgroundColor,
  ctaText,
  ctaBackgroundColor,
  ctaLink,
  productImage,
  logoImage,
  backgroundImage,
  collectionName,
}: MastheadCarouselItemProps) => {
  const { state } = useStore();

  return (
    <MastheadWrapper backgroundImage={backgroundImage}>
      <MastheadContainer>
        <DescriptionWrapper backgroundColor={descriptionBackgroundColor}>
          {logoImage && (
            <LogoAndDescriptionWrapper>
              <ImageDesktopWrapper>
                <DesktopLogoImage src={logoImage} fill quality={90} alt={`${collectionName} logo`} priority />
              </ImageDesktopWrapper>

              <ImageMobileWrapper>
                <MobileLogoImage
                  src={logoImage}
                  width={218}
                  height={51}
                  quality={90}
                  alt={`${collectionName} logo`}
                  priority
                />
              </ImageMobileWrapper>
            </LogoAndDescriptionWrapper>
          )}

          <InfoContentWrapper>
            <Paragraph size="large" color={getColorProp(descriptionTextColor)} className="text-center">
              {descriptionText}
            </Paragraph>
          </InfoContentWrapper>
          <ButtonPrimary
            color={`${ctaBackgroundColor === 'white' ? 'primary-blurple' : 'dark'}`}
            href={ctaLink}
            onClick={() =>
              posthog.capture(`homepage_masthead_cta_click`, {
                creator: collectionName,
                isUserLoggedIn: isUserLoggedIn(state.user),
              })
            }
          >
            {ctaText}
          </ButtonPrimary>
        </DescriptionWrapper>
        <div>
          <a
            href={ctaLink}
            onClick={() =>
              posthog.capture(`homepage_masthead_image_click`, {
                creator: collectionName,
                isUserLoggedIn: isUserLoggedIn(state.user),
              })
            }
          >
            <DesktopPlushieImageWrapper>
              <Image src={productImage} width={500} height={500} alt={`${collectionName} products`} priority />
            </DesktopPlushieImageWrapper>
            <MobilePlushieImageWrapper>
              <Image src={productImage} width={282} height={282} alt={`${collectionName} products`} priority />
            </MobilePlushieImageWrapper>
          </a>
        </div>
      </MastheadContainer>
    </MastheadWrapper>
  );
};
export default MastheadCarouselItem;
